import { generateAttendeeNamePrompt } from "@pique/core/modules/properties/services/attendee-id-utils";
import type { GoogleCalendarEvent } from "@pique/core/modules/properties/services/calendar-types";
import type { Update } from "@tauri-apps/plugin-updater";
import { atom } from "jotai";
import { atomWithStorage } from "jotai/utils";

export const settingModalScrollContainerAtom = atom<HTMLElement | null>(null);

export const otaUpdateDataAtom = atom<Update | null>(null);

export const isInApplicationsFolderAtom = atom<boolean>(true);

export const calendarEventsAtom = atom<GoogleCalendarEvent[]>([]);

export const calendarEventsDisplayPreferenceAtom = atomWithStorage<string>(
	"affine-calendar-events-display",
	"yes",
);

export const clickedEventsAtom = atomWithStorage<string[]>(
	"affine-clicked-events",
	[],
);

// Calendar notifications preference (yes/no). Controls upcoming meeting notifications.
export const calendarEventsNotificationPreferenceAtom = atomWithStorage<string>(
	"affine-calendar-events-notifications",
	"yes",
);

export const autoDetectMeetingsPreferenceAtom = atomWithStorage<boolean>(
	"pique-auto-detect-meetings",
	false,
);

export const startOnLoginPreferenceAtom = atomWithStorage<boolean>(
	"pique-start-on-login",
	false,
);

export const updateChannelPreferenceAtom = atomWithStorage<boolean>(
	"pique-canary-updates",
	true,
);

export const showSlideUploadButtonAtom = atomWithStorage<boolean>(
	"pique-show-slide-upload-button",
	false,
);

export const showEdgelessToolsMenuAtom = atomWithStorage<boolean>(
	"pique-show-edgeless-tools-menu",
	false,
);

export const showCatchMeUpButtonAtom = atomWithStorage<boolean>(
	"pique-show-catch-me-up-button",
	false,
);

export const useGeminiTranscriptionAtom = atomWithStorage<boolean>(
	"pique-use-gemini-transcription",
	false,
);

export const enableTrendLabelsAtom = atomWithStorage<boolean>(
	"pique-enable-trend-labels",
	true,
);

export const enablePicoFocusModeAtom = atomWithStorage<boolean>(
	"pique-enable-pico-focus-mode",
	false,
);

export const PICO_SDK_ENABLED_STORAGE_KEY = "pique-enable-pico-sdk";
export const enablePicoSdkAtom = atomWithStorage<boolean>(
	PICO_SDK_ENABLED_STORAGE_KEY,
	false,
);

export const enableTranslucentModeAtom = atomWithStorage<boolean>(
	"pique-enable-translucent-mode",
	false,
);

export const AUGMENTATION_MODEL_DEFAULT = "gemini-3.5-flash";

export const detailLevelPreferenceAtom = atomWithStorage<string>(
	"pique-detail-level-preference",
	"balanced",
);

export const nudgesPreferenceAtom = atomWithStorage<string>(
	"pique-nudges-preference",
	"light",
);

export const noteStylePreferenceAtom = atomWithStorage<string>(
	"pique-note-style-preference",
	"outline",
);

export const AUGMENT_PROMPT_BASE_DEFAULT = `You are an AI note-enhancement expert specializing in filling gaps and refining meeting notes.

The user recently participated in a meeting and tried to take notes capturing the important parts. In light of the meeting transcription, your task is to enhance the user's notes to fill in any gaps or missing information from the discussion.

TRANSCRIPTION QUALITY NOTE: The transcription may contain errors common to speech-to-text systems:
- Misheard words or phrases (especially technical terms, names, numbers)
- Missing punctuation or incorrect capitalization
- Filler words, false starts, or incomplete sentences
- Background noise artifacts or speaker confusion
When you encounter unclear or potentially incorrect transcription segments, use context clues and your business knowledge to infer the most likely intended meaning.

Your improvements should be detailed yet targeted:
- Add specific numbers, metrics, and deadlines from the transcription
- Insert context about decisions, rationale, and implications
- Expand on technical terms with relevant business context
- Include budget figures, timelines, and deliverables mentioned in the meeting
- Never attribute statements to specific people unless there are explicit direct assignments

Your task is -
`;

export const augmentPromptBaseAtom = atomWithStorage<string>(
	"pique-augment-prompt-base",
	AUGMENT_PROMPT_BASE_DEFAULT,
);

export const MEETING_OVERVIEW_REQUIREMENT_DEFAULT =
	'"meetingOverview": a concise 15-25 word summary focusing only on key decisions and outcomes, no timestamps, no quotes, neutral tone.\n"meetingTitle": a brief 3-6 word title that BEST captures the main purpose or topic of the meeting (e.g., "Q4 Planning Review", "Security Audit Discussion").';

export const AUGMENT_PROMPT_ACTION_ITEMS_DEFAULT = `ACTION ITEMS - For extracting tasks, assignments, and the meeting overview:

CRITICAL OVERRIDE: Do NOT assign any action items to the current user by default. Only assign tasks when the transcript explicitly shows someone being assigned a task through direct language like "John, can you..." or "I'll handle..."

MEETING CONTEXT ANALYSIS:
First, analyze the transcript to understand the meeting type and context:
- Is this a 1:1 meeting between two people? (Look for consistent back-and-forth between two speakers)
- Is this a team/group meeting? (Multiple participants discussing together)
- Is this a presentation/lecture? (One primary speaker presenting to an audience)
- Is this a webinar/training? (Educational content with Q&A)
- Are there clear speaker identities mentioned? (Names used in conversation)

Use this context to better understand action item ownership:
- In 1:1 meetings, tasks often alternate between participants
- In team meetings, look for explicit name mentions for assignments
- In presentations, action items may be general recommendations rather than specific assignments
- If speaker identity is unclear, be conservative with assignments

Identify ALL action items mentioned in the transcript and return them in the "actionItems" array:

- Look for commitments, tasks, follow-ups, and deliverables mentioned in the meeting
- Include "text" field with succinct self contained action description
- Include "context" field with succinct but detailed helpful summarized context capturing transcript surroundings and timestamp
- Include "assignTo" array with email addresses ONLY when assignments are explicitly clear from context
- Look for explicit phrases like "John, can you...", "Sarah will handle...", "I'll take care of..."
- Match names mentioned in the transcript to the attendee list above (format: Name (email)) and use the email for assignTo
- CRITICAL: If no clear assignment or if speakers are ambiguous, omit the "assignTo" field entirely - NEVER default to the current user or any attendee
- IGNORE any mention of "current user" - only assign tasks when there are explicit assignments in the transcript itself
- Include "breakdownSteps" array with exactly 3 highly specific and HIGHLY SUCCINCT steps that break down the action item:
  * Each step should have: id, title, description
  * Titles: 4-6 words, action-focused (e.g., "Review security vulnerabilities", "Update project timeline")
  * Descriptions: 10-15 words, include specific transcript details (e.g., "Analyze SQL injection, XSS, authentication bypass from audit", "Update Sprint 12-14 estimates for API endpoints")
  * Steps MUST include key transcript details (numbers, tools, processes) but be concise
  * Steps must sequentially build towards completion of the exact action item described

CRITICAL: You MUST always identify and return ALL action items from the transcript.

MEETING OVERVIEW SUMMARY:
- ${MEETING_OVERVIEW_REQUIREMENT_DEFAULT}
- Keep the overview factual, business-like, and self-contained; it should make sense without reading the action items.

RESPONSE FORMAT - Return an object containing the meeting title, overview and action items (ALWAYS include all keys even if actionItems is empty):
{
  "meetingTitle": "Q4 Launch Planning Review",
  "meetingOverview": "Team confirmed Q4 launch milestones are on track with security remediation as the only open risk.",
  "actionItems": [
    {
      "text": "Schedule Q4 planning meeting with all stakeholders by end of week",
      "context": "David suggested exploring partnership opportunities with Aventino and ATIA for distribution and pilots. He offered to provide introductions to both. [00:16]",
      "assignTo": ["sarah.manager@company.com"],
      "breakdownSteps": [
        {
          "id": "step-1-identify",
          "title": "Identify all stakeholders",
          "description": "Create list of internal and external stakeholders needed for Q4 planning"
        },
        {
          "id": "step-1-schedule",
          "title": "Find meeting time",
          "description": "Check calendars and propose 2-3 time slots that work for all stakeholders"
        },
        {
          "id": "step-1-send",
          "title": "Send meeting invites",
          "description": "Create agenda and send calendar invites with Q4 planning objectives"
        }
      ]
    },
    {
      "text": "Send weekly status report to executive team",
      "context": "Weekly cadence established to keep executives informed of progress and blockers. [15:22]",
      "breakdownSteps": [
        {
          "id": "step-3-gather",
          "title": "Extract Q4 revenue metrics",
          "description": "Pull $2.4M revenue, 15% YoY growth, acquisition costs from Salesforce"
        },
        {
          "id": "step-3-create",
          "title": "Draft partnership summary",
          "description": "Create Aventino and ATIA distribution channel and pilot metrics report"
        },
        {
          "id": "step-3-distribute",
          "title": "Send Friday report",
          "description": "Distribute to CEO, CTO, VP Sales by 5PM via executive Slack"
        }
      ]
    }
  ]
}`;

export const augmentPromptActionItemsAtom = atomWithStorage<string>(
	"pique-augment-prompt-action-items",
	AUGMENT_PROMPT_ACTION_ITEMS_DEFAULT,
);

export const SOCIAL_FEED_PROMPT_DEFAULT = `SOCIAL FEED EXTRACTION

Task: Extract PERSONAL life facts about attendees that could be used for small talk and building personal connections in future meetings. Please ensure these facts are appropriate for a professional setting and avoid any sensitive or confidential information.

WHAT TO INCLUDE (personal life only):
- Hobbies and interests (e.g., "plays guitar", "into hiking")
- Family mentions (e.g., "has a daughter starting college", "getting a puppy")
- Travel and vacations (e.g., "going to Italy next month", "just got back from Japan")
- Personal milestones (e.g., "just moved to a new house", "training for a marathon")
- Sports teams they follow, books they're reading, shows they're watching
- Weekend plans or activities outside work

WHAT TO EXCLUDE (do not include):
- Work tasks, projects, or action items
- Work tools or software mentions (Squarespace, Ghost, Slack, etc.)
- Professional responsibilities or deadlines
- Anything related to their job duties
- Company or client names in work context

STRICT RULES:
- AttendeeEmail MUST be one of the provided allowed emails.
- Only include if you are highly confident this is a PERSONAL fact, not work-related.
- If unsure whether something is personal or work, exclude it.
- If no suitable personal items exist, return an empty array [].
- Keep items brief, specific, positive, and non-sensitive.

Array item schema:
- attendeeEmail: string (MUST be in allowed list)
- attendeeName: string (optional)
- text: string (short, friendly personal fact)
- confidence: number between 0 and 1
- source: string (optional title or context)
- timestamp: string (optional)
`;

export const socialFeedPromptAtom = atomWithStorage<string>(
	"pique-social-feed-prompt",
	SOCIAL_FEED_PROMPT_DEFAULT,
);

export const AUGMENT_PROMPT_DOCUMENT_BASE_TEMPLATE_DEFAULT = `DOCUMENT PLAN - Build a clean, well-structured document with unified content insertion.

GOAL: Create a holistic, beautifully formatted final document that:
- Identifies and includes topics or points from the transcription that are missing from the notes
- Repositions existing blocks when that improves narrative and structure
- Uses headings and bullets to improve readability, but keeps original text visibly distinct from AI additions

STRUCTURE TEMPLATE:
1. Identify main topics/themes from the transcript
2. For each main topic, create a heading followed by 2-4 key bullet points
3. Use clear, actionable bullet points that capture the most important highlights
4. Organize information hierarchically: Topic → Key Points → Supporting Details

MISSING CONTENT GUIDELINES:
- You MUST identify topics or points from the transcription that are completely absent from the notes
- If the notes seem complete, look deeper for nuanced details, context, or background information from the transcript
- Include specific numbers, metrics, budgets, and quantitative data mentioned
- Add context about decisions, rationale, and implications
- Extract timestamp from transcript where content was discussed (format MM:SS or HH:MM:SS)
- Focus on the most important missing information that adds significant business value
- Create clean, scannable notes that follow best practices
- Skip any action items as they are captured separately
- CRITICAL: Ensure you capture important missing information from the ENTIRE transcript, not just the start

FORMAT RULES:
- Use "insert-heading" operations for main topics/sections (e.g., "Budget Discussion", "Technical Requirements")
- Use "insert-content" operations with "bullet" format for key points under each topic
- Use "insert-content" with "text" format only for standalone paragraphs that don't fit bullet structure
- Include the specific timestamp where each point was discussed in the transcript
- Keep text concise but complete - each point should be self-contained and understandable

POSITIONING RULES:
- When user has existing notes: Use position field with valid blockIds to integrate new content
- When no existing notes: Omit position field entirely - content will be added sequentially
- Never make up block IDs - only use actual IDs from the blocks array in pageContent

OUTPUT FORMAT (STRICT JSON):

Return STRICT JSON with a single key:
- "plan": Unified document plan object following the structure and positioning rules described here

Do NOT include meetingOverview or actionItems in this response - those are produced with the dedicated action items prompt.
`;

export const AUGMENT_PROMPT_EXAMPLE_WITH_NOTES_DEFAULT = `{
  "plan": {
    "operations": [
      // IMPORTANT: Look at the blocks array in pageContent for actual blockIds (e.g., "Ec6QJ5vSuP", "bGruCDpIXp")
      // PREFER positioned inserts to build structure around existing notes
      // CRITICAL: Capture ALL missing content from the ENTIRE transcript - use positioned inserts to integrate with existing notes in the best possible way

      // Best practice: Use insert-heading for section headers, positioned relative to existing blocks
      { "type": "insert-heading", "data": { "text": "Budget Discussion", "level": "h5", "position": {"beforeBlockId": "USE_ACTUAL_BLOCK_ID_FROM_BLOCKS_ARRAY"} } },

      // Insert ALL missing content relative to existing blocks using actual IDs from blocks array
      { "type": "insert-content", "data": { "text": "Quarterly budget was discussed with proposed 15% increase", "format": "bullet", "timestamp": "05:45", "position": {"afterBlockId": "USE_REAL_BLOCK_ID"} } },
      { "type": "insert-content", "data": { "text": "The team agreed to prioritize infrastructure investments", "format": "bullet", "timestamp": "06:12", "position": {"afterBlockId": "ACTUAL_BLOCK_ID"} } },
      { "type": "insert-content", "data": { "text": "CFO mentioned Q1 projections need revision based on market conditions", "format": "bullet", "timestamp": "08:30", "position": {"afterBlockId": "ACTUAL_BLOCK_ID"} } },

      // Reposition blocks to improve structure
      { "type": "reposition-block", "data": { "blockId": "EXISTING_BLOCK_ID", "position": {"afterBlockId": "ANOTHER_BLOCK_ID"} } },

      // Add new topic sections for content not covered by user notes
      { "type": "insert-heading", "data": { "text": "Technical Requirements", "level": "h5", "position": {"at": "bottom"} } },
      { "type": "insert-content", "data": { "text": "API rate limiting needs to be implemented before launch", "format": "bullet", "timestamp": "15:20" } },
      { "type": "insert-content", "data": { "text": "Database migration scheduled for next sprint", "format": "bullet", "timestamp": "16:45" } }
    ]
  }
}`;

export const AUGMENT_PROMPT_EXAMPLE_NO_NOTES_DEFAULT = `{
  "plan": {
    "operations": [
      // When no existing notes, create a complete structured document from transcript
      // Follow the template: Main Topics -> Key Points -> Supporting Details
      // CRITICAL: Capture ALL important content from the ENTIRE transcript

      // First main topic section
      { "type": "insert-heading", "data": { "text": "Budget Review & Resource Planning", "level": "h5" } },
      { "type": "insert-content", "data": { "text": "Q4 budget approved at $2.4M with 15% increase from Q3", "format": "bullet", "timestamp": "05:45" } },
      { "type": "insert-content", "data": { "text": "Development team requesting 2 additional senior developers for sprint capacity", "format": "bullet", "timestamp": "06:12" } },
      { "type": "insert-content", "data": { "text": "Marketing budget allocated $180K for LinkedIn campaigns and trade publications", "format": "bullet", "timestamp": "06:30" } },

      // Second main topic section
      { "type": "insert-heading", "data": { "text": "Technical Architecture Discussion", "level": "h5" } },
      { "type": "insert-content", "data": { "text": "Migration to microservices architecture approved for Q1 implementation", "format": "bullet", "timestamp": "12:10" } },
      { "type": "insert-content", "data": { "text": "Database sharding strategy required before scaling to 1M users", "format": "bullet", "timestamp": "12:25" } },
      { "type": "insert-content", "data": { "text": "Load testing reveals current infrastructure handles 50K concurrent users", "format": "bullet", "timestamp": "12:40" } },

      // Third main topic section
      { "type": "insert-heading", "data": { "text": "Security & Compliance Updates", "level": "h5" } },
      { "type": "insert-content", "data": { "text": "Security audit revealed 3 critical vulnerabilities requiring immediate patches", "format": "bullet", "timestamp": "18:15" } },
      { "type": "insert-content", "data": { "text": "GDPR compliance review scheduled with legal team for next week", "format": "bullet", "timestamp": "18:45" } },
      { "type": "insert-content", "data": { "text": "External security consultant needed - estimated cost $25K before December launch", "format": "bullet", "timestamp": "19:05" } },

      // Standalone paragraph for context (using "text" format)
      { "type": "insert-content", "data": { "text": "The team emphasized that all security updates must be completed before the public beta launch to ensure customer data protection and maintain compliance with industry standards.", "format": "text", "timestamp": "19:30" } }
    ]
  }
}`;

export const AUGMENT_PROMPT_POSITIONING_RULES_DEFAULT = `
CRITICAL POSITIONING RULES:
- ALWAYS examine the blocks array in pageContent for actual block IDs
- PREFER positioned inserts: {"afterBlockId": "ACTUAL_ID"} or {"beforeBlockId": "ACTUAL_ID"}
- Build a cohesive document by inserting headings and content near related existing blocks
- Group related content together using positioned inserts
- Never make up block IDs - use actual IDs from the blocks array`;

export const augmentPromptDocumentPlanAtom = atomWithStorage<string>(
	"pique-augment-prompt-document-plan",
	AUGMENT_PROMPT_DOCUMENT_BASE_TEMPLATE_DEFAULT,
);

export const augmentPromptExampleWithNotesAtom = atomWithStorage<string>(
	"pique-augment-prompt-example-with-notes",
	AUGMENT_PROMPT_EXAMPLE_WITH_NOTES_DEFAULT,
);

export const augmentPromptExampleNoNotesAtom = atomWithStorage<string>(
	"pique-augment-prompt-example-no-notes",
	AUGMENT_PROMPT_EXAMPLE_NO_NOTES_DEFAULT,
);

export const augmentPromptPositioningRulesAtom = atomWithStorage<string>(
	"pique-augment-prompt-positioning-rules",
	AUGMENT_PROMPT_POSITIONING_RULES_DEFAULT,
);

export interface PromptSection {
	title: string;
	description: string;
	atom: any;
	defaultValue: string;
}

export interface PromptGroup {
	id: string;
	title: string;
	description: string;
	sections: PromptSection[];
}

export const generateAttendeePromptSegment = (
	attendeeVarName: string = "attendee",
) => {
	return `For attendee addressing, use this utility: ${generateAttendeeNamePrompt.toString()}. Apply as: \${generateAttendeeNamePrompt(${attendeeVarName})}.`;
};

export const PROMPT_GROUPS: PromptGroup[] = [
	{
		id: "note-augmentation",
		title: "Note Augmentation System",
		description:
			"AI pipeline for enhancing meeting notes with transcription data",
		sections: [
			{
				title: "Base Instructions",
				description:
					"Core AI behavior, role definition, and enhancement guidelines",
				atom: augmentPromptBaseAtom,
				defaultValue: AUGMENT_PROMPT_BASE_DEFAULT,
			},
			{
				title: "Document Structuring Plan",
				description:
					"High-level plan to reposition existing blocks and insert headings/content for a clean, holistic document",
				atom: augmentPromptDocumentPlanAtom,
				defaultValue: AUGMENT_PROMPT_DOCUMENT_BASE_TEMPLATE_DEFAULT,
			},
			{
				title: "Example Output - With User Notes",
				description:
					"Example JSON response when user has existing notes to enhance",
				atom: augmentPromptExampleWithNotesAtom,
				defaultValue: AUGMENT_PROMPT_EXAMPLE_WITH_NOTES_DEFAULT,
			},
			{
				title: "Example Output - No User Notes",
				description: "Example JSON response when starting from transcript only",
				atom: augmentPromptExampleNoNotesAtom,
				defaultValue: AUGMENT_PROMPT_EXAMPLE_NO_NOTES_DEFAULT,
			},
			{
				title: "Positioning Rules",
				description:
					"Critical rules for positioning content relative to existing blocks",
				atom: augmentPromptPositioningRulesAtom,
				defaultValue: AUGMENT_PROMPT_POSITIONING_RULES_DEFAULT,
			},
			{
				title: "Action Item Instructions",
				description:
					"Rules for extracting and assigning action items from meeting transcripts",
				atom: augmentPromptActionItemsAtom,
				defaultValue: AUGMENT_PROMPT_ACTION_ITEMS_DEFAULT,
			},
		],
	},
];
